import { FilterNode } from '@blisspointmedia/bpm-types/dist/querying';
import { DimensionId } from '@blisspointmedia/bpm-types/dist/querying/ids';
import { QueryClient, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CARD_SIZES,
  CardSize,
  ChartId,
  ChartType,
  Dashboard,
  DashboardId,
  DashboardInput,
  DashboardLayoutConfig,
  DashboardMeta,
  DashboardPlacement,
  DashboardTemplateId,
  DashboardTemplateMeta,
  InstantiateDashboardTemplateResponse,
  isCardSizeAllowed,
  ListDashboardTemplatesResponse,
  RoleLevels,
  SaveDashboardResponse
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { awaitJSON, DashboardingLambdaFetch } from '../../../utils/fetch-utils';
import { isFilterNodeList, isIgnoreList } from '../dashboard-filter-utils';
import { CardType, ChartCardConfig, DashboardCard, DashboardConfig } from '../dashboard-types';
import { DEFAULT_CARD_SIZE, GRID_COLUMNS, SIZE_DIMENSIONS } from '../dashboard-constants';
import { compactBySection, findBestPosition, packedRows, sizeDimensions } from '../dashboard-utils';
import { retryUnlessClientError } from '../../Charts/hooks/useChartData';
import { useRecordPermissions } from '../../Charts/hooks/useRecordPermissions';

// Sentinel id used before any dashboard is persisted (no real uuid yet).
export const UNSAVED_ID = 'default';

/**
 * Whether this user may change one dashboard.
 *
 * Lives here, beside `UNSAVED_ID`, because two surfaces ask it and they must not be able to answer
 * differently: DashboardView (Edit Dashboard, a layout save, a card removed from view mode) and
 * AgentDrawer (appending an agent-built chart, from outside the dashboard page). It was written out
 * in both, in two spellings.
 *
 * An UNSAVED dashboard is never gated. Before the first save there is no row and so no levels, and a
 * check on the edit level alone would hide Edit Dashboard and block the very save that creates the
 * row — for every user, administrators included, at any company with no dashboard yet.
 * `useRecordPermissions` already returns true when a level is absent; the explicit `UNSAVED_ID` test
 * is written out so the intent is visible rather than inferred.
 *
 * A dashboard is governed by its OWN levels, never by the charts on it — a chart the user cannot
 * change draws its own View only badge, and one they cannot see draws a locked card.
 */
export const useCanEditDashboard = (config?: DashboardConfig): boolean => {
  const { canEdit } = useRecordPermissions(config);
  return !config || config.id === UNSAVED_ID || canEdit;
};

// ─── API envelope <-> FE DashboardConfig converters ──────────────────────────

// A rejected stored value goes to the `...Raw` field only, so it is written back but never used.
const ignoreListFields = (
  stored: DimensionId[] | undefined
): Pick<ChartCardConfig, 'ignoreDashboardFilters' | 'savedIgnoreDashboardFiltersRaw'> => {
  if (stored === undefined) return {};
  return isIgnoreList(stored)
    ? { ignoreDashboardFilters: stored }
    : { savedIgnoreDashboardFiltersRaw: stored };
};

const defaultFiltersFields = (
  stored: FilterNode[] | undefined
): Pick<DashboardConfig, 'defaultFilters' | 'savedDefaultFiltersRaw'> => {
  if (stored === undefined) return {};
  return isFilterNodeList(stored) ? { defaultFilters: stored } : { savedDefaultFiltersRaw: stored };
};

const placementToCard = (placement: DashboardPlacement, i: number): DashboardCard => ({
  id: `placement-${i}-${placement.chartId}`,
  type: CardType.CHART,
  // Absent on every placement saved before sizes existed, and on any the agent emits without one.
  size: placement.size ?? DEFAULT_CARD_SIZE,
  position: placement.position,
  config: {
    chartId: placement.chartId,
    ...ignoreListFields(placement.ignoreDashboardFilters)
  }
});

/**
 * The ONLY way to read placements off a fetched dashboard. Every path that renders or re-saves a
 * layout has to go through here — kept centralised (rather than three inline `d.layout.placements`
 * reads) so a future migration has exactly one place to land, the same reason it existed before.
 *
 * There USED TO be a version-gated rescale here: multiply `position.y` by the standard card's row
 * span whenever `d.configVersion < 2`, on the theory that a v1 layout indexed y against old 540px
 * rows. It was removed because the version stamp it trusted is a lie in production — the deployed
 * bpm-lambdas pins a bpm-types release below 6.0.0, so `saveDashboard` always writes back
 * `configVersion: 1`, including for a dashboard created and saved today. The rescale therefore fired
 * on EVERY read, and because a save persists whatever coordinates it was handed while the server
 * keeps stamping the row `1`, the NEXT load rescaled the already-rescaled values again — 10x per
 * save/reload cycle. Confirmed live: grid-row starts of `1, 1, 1, 101, 101, 201, 301, 401, 501, 601`
 * where `1, 1, 1, 11, 11, 21, 31, 41, 51, 61` were correct.
 *
 * Trusting stored coordinates as-is (no version-gated rescale) is safe for two reasons: no
 * dashboards are live yet, so any layout already skewed by the old bug is disposable rather than
 * something that must be migrated forward; and unlike the deleted rescale — which GUESSED a scale
 * factor off a stamp that lies — the repair for a skewed layout is `compactBySection`, applied
 * unconditionally below rather than gated on any version. The real upstream fix is deploying
 * bpm-lambdas on bpm-types >= 6.0.0 so `configVersion` becomes truthful — but this frontend
 * deliberately no longer reads or depends on that stamp, so that deploy (whenever it lands) cannot
 * silently re-trigger a rescale here. Do not resurrect a version check on this path.
 */
const migratedPlacements = (d: Dashboard): DashboardPlacement[] => d.layout.placements;

// Exported for its unit test. The two role levels are the reason it earns one: they sit on the
// ENVELOPE beside `layout`, not inside it, so it is easy to drop them here and equally easy not to
// notice — every gate would then read `undefined` and quietly treat the dashboard as editable.
//
// `cards` runs through `compactBySection` (not a bare `.map`) so every dashboard is normalised to a
// compacted layout as it is READ, not only when explicitly requested. This is safe to do
// unconditionally, with no flag and no opt-out, because every WRITE path already forces the
// same invariant: `DashboardBuilder`'s handleMoveCard, handleRemoveCard, resizeCardInList, its
// section-remove handler, and the agent's pending-resize path all end in `autoCompact` (or this same
// `compactBySection`) before saving. There is therefore no way for a user to express "leave a
// deliberate vertical gap here" through the UI — a gap can only be leftover corruption from the
// since-fixed `position.y *= 10` bug (see the block above), never an intentional layout. Compacting
// here enforces an invariant the writes already hold rather than discarding user intent, and it is a
// no-op for any dashboard that was written through the normal paths, since those are compacted
// already. `sections` is always `[]` on this path (see below), so `compactBySection`'s grouping
// degenerates to one group of loose cards — `autoCompact` alone would do the same today, but
// `compactBySection` is used anyway so sectioned layouts stay correctly grouped if sections are ever
// populated here.
//
// Array order arrives straight from `layout.placements` (via `migratedPlacements`, in the order the
// server returns it) and is AUTHORITATIVE, full stop — `configToLayout` writes `config.cards` into
// `layout.placements` in that same array order, that is what gets persisted as jsonb, and this
// function reads it back unchanged. Positions are normalised by `compactBySection` below, but order
// is never re-derived from them.
//
// This function USED TO `.sort(byGridOrder)` the placements before compacting, to bootstrap array
// order from stored position for dashboards saved before explicit ordering existed. At the time
// reading order and array order were always the same thing, so the sort was harmless. Vertical
// stacking deliberately broke that equivalence: a card stacked beneath another (same x, half height)
// sorts AFTER a card placed beside the pair, even though it comes earlier in the array the user
// actually built. So the sort now DESTROYS exactly the intent it was meant to recover — saving a
// stacked layout and reloading it silently unstacked the second card and dropped it to a new row.
// Do not re-add a positional sort here, or anywhere else on this read path: array order is the only
// record of what the user meant, and once cards can stack, no `(y, x)` derivation can recover it.
//
// This is READ-ONLY normalisation: it recomputes the shape of the object handed back from a GET, and
// nothing here calls `saveDashboard`. Loading a page must never write to the server — that would bump
// `modified_at` / `last_modified_by` on every view, and a viewer without edit rights could not save
// anyway. The repaired layout persists naturally the next time the user saves for a real reason.
export const dashboardToConfig = (d: Dashboard): DashboardConfig => ({
  id: d.id,
  clientId: d.company,
  name: d.name,
  isDefault: d.isDefault,
  cards: compactBySection(migratedPlacements(d).map(placementToCard)),
  sections: [],
  defaultDateConfig: d.layout.defaultDateConfig,
  ...defaultFiltersFields(d.layout.defaultFilters),
  viewRoleRequired: d.viewRoleRequired,
  editRoleRequired: d.editRoleRequired
});

// Exported for its unit test. A saved filter value the dashboard could not use is written back
// unchanged, never dropped.
export const configToLayout = (config: DashboardConfig): DashboardLayoutConfig => {
  const defaultFilters = config.defaultFilters ?? config.savedDefaultFiltersRaw;
  return {
    ...(config.defaultDateConfig ? { defaultDateConfig: config.defaultDateConfig } : {}),
    ...(defaultFilters !== undefined ? { defaultFilters } : {}),
    placements: config.cards.flatMap(c => {
      if (c.type !== CardType.CHART || !c.config) return [];
      const { chartId, ignoreDashboardFilters, savedIgnoreDashboardFiltersRaw } = c.config;
      const ignore = ignoreDashboardFilters ?? savedIgnoreDashboardFiltersRaw;
      return [
        {
          chartId,
          position: c.position,
          size: c.size,
          ...(ignore !== undefined ? { ignoreDashboardFilters: ignore } : {})
        }
      ];
    })
  };
};

// ─── Auto-layout for agent-built dashboards ──────────────────────────────────

/** One chart to place, with the preset it should be placed at. */
export interface SizedChart {
  chartId: ChartId;
  size: CardSize;
  /**
   * The chart's type, when the caller knows it. Read only to check the size-capability matrix
   * before promoting a card in `fillRowGaps` — optional because the agent's emitted list may omit
   * it, and an unknown type simply skips the check.
   */
  type?: ChartType;
  ignoreDashboardFilters?: DimensionId[];
}

// Lay out an ordered list of charts into placements, appending to any existing ones. Sizes are
// per-chart rather than uniform, so a `wide` table and a `standard` graphic pack together without
// overlapping. Positions are assigned the same way the grid does (findBestPosition packs top-left
// first), so the agent's chart order is preserved left-to-right, top-to-bottom. Reused by the agent
// drawer to build a whole dashboard and to append a chart to the current one.
// The preset whose width is exactly `columns`, if there is one. Derived from SIZE_DIMENSIONS rather
// than written out, so a new preset joins the lookup by existing.
const presetOfWidth = (columns: number): CardSize | undefined =>
  CARD_SIZES.find(size => SIZE_DIMENSIONS[size].width === columns);

/**
 * Close interior holes in an agent-emitted order by promoting the cards that strand them.
 *
 * The agent is told to order charts so a row fills exactly, but it is a counting rule it gets
 * wrong. A row that is under-full and has MORE charts after it is a hole: the next card could not
 * fit the remainder and wrapped.
 *
 * With two presets the only such row was a lone standard, so the old rule looked for exactly one
 * card and widened it. Three presets breaks that -- two thirds strand 4 of 12 columns -- so the
 * rule is now: for an under-full non-final row of N cards, find the preset whose width is exactly
 * `GRID_COLUMNS / N` and promote every card in the row to it.
 *
 *   1 card  -> width 12 -> wide        (reproduces the old behaviour exactly)
 *   2 cards -> width 6  -> standard
 *   3 cards -> width 4  -> one-third   (already full unless something taller shares the row)
 *
 * An N with no exact preset is left alone rather than approximated. So is a card whose chart type
 * disallows the target size -- a squeezed table is worse than a gap -- and so is a card the target
 * would SHRINK: closing a 2-column gap is not worth making a chart less readable, and this is gap
 * FILLING, not re-layout.
 *
 * The LAST row is never touched: a part-width row at the bottom is a normal way for a dashboard to
 * end, not a hole.
 *
 * Rows come from `packedRows` -- the same walk `autoCompact` positions with -- rather than a second
 * left-to-right column count here. That matters as soon as two cards stack: a flat count reads
 * `[one-third, one-third, standard]` as overflowing at 14 columns and "fixes" a hole the packer never
 * creates (it stacks the pair into one 4-column slot and fits all three in a single 10/12 row),
 * widening both one-thirds and splitting a row that packed fine.
 */
const fillRowGaps = (
  charts: SizedChart[],
  isAllowed: (type: ChartType, size: CardSize) => boolean = isCardSizeAllowed
): SizedChart[] => {
  const rows = packedRows(charts);
  const promotions = new Map<SizedChart, CardSize>();

  rows.forEach(({ items, columnsUsed }, i) => {
    if (i === rows.length - 1 || columnsUsed >= GRID_COLUMNS) return;
    if (GRID_COLUMNS % items.length !== 0) return;
    const target = presetOfWidth(GRID_COLUMNS / items.length);
    if (!target) return;
    const targetWidth = sizeDimensions(target).width;
    items.forEach(chart => {
      if (sizeDimensions(chart.size).width >= targetWidth) return;
      // No type (the agent may omit it) means no rule to check -- promote. A type that forbids the
      // target keeps its own size, and the row keeps its hole.
      if (chart.type && !isAllowed(chart.type, target)) return;
      promotions.set(chart, target);
    });
  });

  if (promotions.size === 0) return charts;
  return charts.map(chart => {
    const size = promotions.get(chart);
    return size ? { ...chart, size } : chart;
  });
};

/**
 * `fillRowGaps` with the size-capability check swapped out. Exported for its unit test ONLY.
 *
 * The real matrix cannot exercise the guard: the only promotion targets are `wide` and `standard`,
 * and every chart type allows both today. Injecting the predicate is how the guard stays pinned
 * against a future rule that does forbid one of them.
 */
export const fillRowGapsWithAllowance = (
  isAllowed: (type: ChartType, size: CardSize) => boolean,
  charts: SizedChart[]
): SizedChart[] => fillRowGaps(charts, isAllowed);

export const autoPlaceChartIds = (
  charts: SizedChart[],
  existing: DashboardPlacement[] = []
): DashboardPlacement[] => {
  const cards: DashboardCard[] = existing.map(placementToCard);
  const placed: DashboardPlacement[] = [...existing];
  // Gap-filling applies to a NET-NEW dashboard only. Appending to one that already has a layout
  // must not re-size anything: a gap there may be deliberate, and the row accounting in fillRowGaps
  // counts from column 0, which an existing partial last row would invalidate.
  const toPlace = existing.length === 0 ? fillRowGaps(charts) : charts;
  for (const { chartId, size, ignoreDashboardFilters } of toPlace) {
    const position = findBestPosition(size, cards) ?? { x: 0, y: 0 };
    const placement: DashboardPlacement = {
      chartId,
      position,
      size,
      ...(ignoreDashboardFilters ? { ignoreDashboardFilters } : {})
    };
    placed.push(placement);
    cards.push(placementToCard(placement, cards.length));
  }
  return placed;
};

// ─── Imperative saves (for the omnipresent agent drawer) ─────────────────────

// Create a brand-new dashboard from an ordered list of sized charts — the agent's whole-dashboard
// build. The server mints the id (omitted) and defaults role requirements. Returns the saved
// Dashboard. Mirrors the saveDashboardConfig body shape (a partial DashboardInput; the server fills
// the rest).
export const createDashboardFromCharts = async (
  cid: string,
  name: string,
  charts: SizedChart[],
  defaultFilters?: FilterNode[]
): Promise<Dashboard> => {
  const dashboard: Partial<DashboardInput> = {
    company: cid,
    name,
    layout: {
      placements: autoPlaceChartIds(charts),
      ...(defaultFilters?.length ? { defaultFilters } : {})
    },
    isDefault: false
  };
  const res = await DashboardingLambdaFetch('/saveDashboard', {
    method: 'POST',
    body: { company: cid, dashboard }
  });
  const { dashboard: saved } = await awaitJSON<SaveDashboardResponse>(res);
  return saved;
};

// Append a chart to an existing dashboard (the context-aware "add to THIS dashboard"). Fetches the
// current layout, auto-places the new chart after the existing ones, and upserts in place (keeps id).
//
// Takes a fully SIZED chart rather than a bare id: the placement written here is the one the card
// keeps, and nothing downstream corrects its size (`placementToCard` passes the stored value
// through, and the card menu deliberately keeps a current-but-disallowed size so it doesn't
// misreport what the card IS). This used to hardcode `size: DEFAULT_CARD_SIZE`, which persisted
// every appended chart at `standard` regardless of type -- a `stat` allows only `one-third`, and even a
// `table` wanted `wide`. Build the argument with `sizedChartForPlan` so the chart-type size matrix
// is applied on this path exactly as it is on the whole-dashboard build.
export const appendChartToDashboard = async (
  cid: string,
  dashboardId: DashboardId,
  chart: SizedChart
): Promise<Dashboard> => {
  const getRes = await DashboardingLambdaFetch('/getDashboard', {
    params: { id: dashboardId, company: cid }
  });
  const current = await awaitJSON<Dashboard>(getRes);
  const dashboard: Partial<DashboardInput> = {
    id: current.id,
    company: cid,
    name: current.name,
    // Spread the CURRENT layout: rebuilding it as `{ placements }` would drop defaultDateConfig
    // (and anything else living in the layout blob) every time the agent appends a chart.
    layout: {
      ...current.layout,
      placements: autoPlaceChartIds([chart], migratedPlacements(current))
    },
    isDefault: current.isDefault
  };
  const res = await DashboardingLambdaFetch('/saveDashboard', {
    method: 'POST',
    body: { company: cid, dashboard }
  });
  const { dashboard: saved } = await awaitJSON<SaveDashboardResponse>(res);
  return saved;
};

/**
 * Point ONE placement at a different chart, leaving the rest of the layout untouched.
 *
 * Keyed by the placement's INDEX, because array order is the one thing the read path preserves
 * exactly: `configToLayout` writes `config.cards` into `layout.placements` in array order and
 * `dashboardToConfig` reads it straight back, so the Nth card on screen is the Nth placement on
 * disk. The index is also what distinguishes two placements of the SAME chart on one dashboard, so
 * editing one card does not repoint its twin.
 *
 * It must NOT match on position. The card on screen carries a RECOMPACTED position --
 * `dashboardToConfig` runs every read through `compactBySection` -- which routinely disagrees with
 * the coordinates stored on the placement it came from (anything written by `autoPlaceChartIds`,
 * which places first-fit, or saved before the 12-column grid). A positional match then finds
 * nothing, and the old code silently re-saved the layout unchanged, orphaning the chart it had
 * just minted.
 *
 * The chart id is still checked, as a guard rather than a key: if the placement at `index` holds a
 * different chart the array has drifted under us, so fall back to the first placement holding the
 * chart we were asked to repoint, and throw if there is none. Never return the list unchanged --
 * the caller is about to save it, and a silent no-op is exactly the failure this replaces.
 *
 * Exported for its unit test: it is pure, and the fetch/save wrapper around it is not.
 */
export const repointPlacements = (
  placements: DashboardPlacement[],
  from: { chartId: ChartId; index: number },
  toChartId: ChartId
): DashboardPlacement[] => {
  const at =
    placements[from.index]?.chartId === from.chartId
      ? from.index
      : placements.findIndex(p => p.chartId === from.chartId);
  if (at === -1) {
    throw new Error(`repointDashboardCard: ${from.chartId} is no longer on this dashboard`);
  }
  return placements.map((p, i) => (i === at ? { ...p, chartId: toChartId } : p));
};

// Repoint a single card at a different chart — the "Show it here" outcome of a dashboard card's Save
// as a new chart: after a fresh chart is minted from the card's working copy, swap THIS placement's
// chartId to the new chart so the card switches over while the shared original (and every other
// dashboard) stays untouched. See `repointPlacements` for why the card is identified by its index.
// Upserts in place (keeps the dashboard id).
export const repointDashboardCard = async (
  cid: string,
  dashboardId: DashboardId,
  from: { chartId: ChartId; index: number },
  toChartId: ChartId
): Promise<Dashboard> => {
  const getRes = await DashboardingLambdaFetch('/getDashboard', {
    params: { id: dashboardId, company: cid }
  });
  const current = await awaitJSON<Dashboard>(getRes);
  const placements = repointPlacements(migratedPlacements(current), from, toChartId);
  const dashboard: Partial<DashboardInput> = {
    id: current.id,
    company: cid,
    name: current.name,
    // Spread the CURRENT layout — see appendChartToDashboard above.
    layout: { ...current.layout, placements },
    isDefault: current.isDefault
  };
  const res = await DashboardingLambdaFetch('/saveDashboard', {
    method: 'POST',
    body: { company: cid, dashboard }
  });
  const { dashboard: saved } = await awaitJSON<SaveDashboardResponse>(res);
  return saved;
};

// ─── Data access ─────────────────────────────────────────────────────────────

const fetchDashboardList = async (cid: string): Promise<DashboardMeta[]> => {
  const res = await DashboardingLambdaFetch('/listDashboards', { params: { company: cid } });
  return awaitJSON<DashboardMeta[]>(res);
};

const dashboardListKey = (cid: string) => ['dashboard-list', cid];

const dashboardListQueryOptions = (cid: string) => ({
  queryKey: dashboardListKey(cid),
  queryFn: () => fetchDashboardList(cid)
});

// The templates this company is eligible for — already filtered server-side, so a template that
// can't work for this client is absent rather than present-and-disabled.
const fetchDashboardTemplates = async (cid: string): Promise<DashboardTemplateMeta[]> => {
  const res = await DashboardingLambdaFetch('/listDashboardTemplates', {
    params: { company: cid }
  });
  return awaitJSON<ListDashboardTemplatesResponse>(res);
};

// Resolve a template into a real dashboard for this company. No preview step by design — see
// InstantiateDashboardTemplateParams.
const instantiateDashboardTemplate = async (
  cid: string,
  templateId: DashboardTemplateId
): Promise<InstantiateDashboardTemplateResponse> => {
  const res = await DashboardingLambdaFetch('/instantiateDashboardTemplate', {
    method: 'POST',
    body: { company: cid, templateId }
  });
  return awaitJSON<InstantiateDashboardTemplateResponse>(res);
};

// Resolve which dashboard to load: an explicit id, else the company's default (or first).
const resolveDashboardId = async (
  queryClient: QueryClient,
  cid: string,
  dashboardId?: string
): Promise<DashboardId | null> => {
  if (dashboardId && dashboardId !== UNSAVED_ID) return dashboardId;
  const list = await queryClient.fetchQuery(dashboardListQueryOptions(cid));
  return (list.find(d => d.isDefault) ?? list[0])?.id ?? null;
};

const fetchDashboard = async (
  queryClient: QueryClient,
  cid: string,
  dashboardId?: string
): Promise<DashboardConfig> => {
  const id = await resolveDashboardId(queryClient, cid, dashboardId);
  if (!id) {
    // No dashboard persisted yet — start empty; the first save creates one.
    return {
      id: UNSAVED_ID,
      clientId: cid,
      name: 'Overview',
      isDefault: true,
      cards: [],
      sections: []
    };
  }
  const res = await DashboardingLambdaFetch('/getDashboard', { params: { id, company: cid } });
  return dashboardToConfig(await awaitJSON<Dashboard>(res));
};

// `roles` is the sharing control's payload, and it is a SEPARATE argument on purpose.
//
// ⚠️ NEVER RESTATE THE LEVELS FROM `config`. Sending them only when a caller passes them is what
// keeps an ordinary save — a layout drag, a rename, an agent append — from overwriting them: the
// backend keeps whatever the row already holds for a level the request omits. Restate them and this
// race loses data silently: user A opens a dashboard, user B narrows its audience, user A drags a
// card, and A's layout save undoes B's change with no error anywhere.
//
// The two levels ride INSIDE `dashboard` on the wire, beside `layout` rather than in it — that is
// the shape `DashboardInput` declares and the shape `saveDashboard` reads (`const requested:
// RoleLevels = dashboard`). Putting them at the top of the body instead fails silently: the handler
// reads no levels and keeps the stored pair.
const postDashboardConfig = async (
  config: DashboardConfig,
  roles?: RoleLevels
): Promise<Dashboard> => {
  const res = await DashboardingLambdaFetch('/saveDashboard', {
    method: 'POST',
    body: {
      company: config.clientId,
      dashboard: {
        // Omit id on an unsaved dashboard so the server mints a uuid; otherwise upsert in place.
        ...(config.id && config.id !== UNSAVED_ID ? { id: config.id } : {}),
        name: config.name,
        layout: configToLayout(config),
        isDefault: config.isDefault,
        ...roles
      }
    }
  });
  const { dashboard } = await awaitJSON<SaveDashboardResponse>(res);
  return dashboard;
};

const saveDashboardConfig = async (
  config: DashboardConfig,
  roles?: RoleLevels
): Promise<DashboardConfig> => dashboardToConfig(await postDashboardConfig(config, roles));

// Returns the saved row itself, not a config, so the list entry written from it is exact.
const createDashboard = (params: { cid: string; name: string }): Promise<Dashboard> =>
  postDashboardConfig({
    id: UNSAVED_ID,
    clientId: params.cid,
    name: params.name,
    isDefault: false,
    cards: [],
    sections: []
  });

// ─── Dashboard list cache ────────────────────────────────────────────────────

// Why these write the list rather than invalidate it: the list isn't mounted while a dashboard is
// open, so an invalidation only marks it stale, and with the app-wide `refetchOnMount: false` going
// back to the list never refetches. We know exactly what was saved, so write it in. An unfetched
// list is left alone — its first mount fetches the truth anyway.

// A just-created dashboard goes FIRST, matching the server's `modified_at desc` order.
export const upsertDashboardInList = (
  queryClient: QueryClient,
  cid: string,
  { layout, ...meta }: Dashboard
): void => {
  queryClient.setQueryData<DashboardMeta[]>(dashboardListKey(cid), list =>
    list ? [meta, ...list.filter(d => d.id !== meta.id)] : list
  );
};

// Only the name, in place: a rename is the one dashboard edit the list shows. Returns the same list
// when nothing changed, so no observer re-renders.
export const renameDashboardInList = (
  queryClient: QueryClient,
  cid: string,
  id: DashboardId,
  name: string
): void => {
  queryClient.setQueryData<DashboardMeta[]>(dashboardListKey(cid), list =>
    list?.some(d => d.id === id && d.name !== name)
      ? list.map(d => (d.id === id ? { ...d, name } : d))
      : list
  );
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

export const useDashboard = (cid: string, dashboardId?: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['dashboard', cid, dashboardId ?? UNSAVED_ID],
    queryFn: () => fetchDashboard(queryClient, cid, dashboardId),
    enabled: Boolean(cid),
    // A 403 is a verdict, not a hiccup. Without this the app-wide `retry: 1` asks a second time for
    // a dashboard it will never be given, which only delays the locked state by a round trip.
    //
    // Deliberately NOT the whole `FAILURE_IS_TERMINAL`: the `retryOnMount: false` half of that
    // exists for the card-level spinner loop, and that loop cannot happen here. DashboardView owns
    // this query and stays mounted while it draws its own spinner, so nothing unmounts to refetch.
    retry: retryUnlessClientError
  });
};

/**
 * The dashboard write's mutation key.
 *
 * ⚠️ A CONTRACT BETWEEN TWO FILES. `useUpdateDashboard` sets it; `DashboardHeader` matches on it via
 * `useIsMutating` to know whether a layout save is still in flight. If the two ever disagree the
 * match silently returns zero, the guard quietly stops guarding, and a chat sharing write can race a
 * layout save again — so the shape is asserted in a unit test rather than left to inspection.
 */
export const dashboardMutationKey = (cid: string, dashboardId: string) => [
  'dashboard-save',
  cid,
  dashboardId
];

// The dashboard's one write. Both callers go through it: a layout/rename save from the builder, and
// the sharing control's level change.
//
// ⚠️ THE VARIABLES ARE ONE OBJECT, NOT TWO ARGUMENTS. `useMutation` calls its `mutationFn` with
// exactly one argument, so a second positional parameter could never be filled — and TypeScript
// reports nothing, because the function is simply used as a unary callback. Written the other way
// the sharing control's Save appears to work and changes no level.
export const useUpdateDashboard = (cid: string, dashboardId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: dashboardMutationKey(cid, dashboardId),
    mutationFn: ({ config, roles }: { config: DashboardConfig; roles?: RoleLevels }) =>
      saveDashboardConfig(config, roles),
    onSuccess: updated => {
      queryClient.setQueryData(['dashboard', cid, dashboardId], updated);
      renameDashboardInList(queryClient, cid, updated.id, updated.name);
    }
  });
};

export const useCreateDashboard = (cid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createDashboard({ cid, name }),
    onSuccess: created => {
      queryClient.setQueryData(['dashboard', cid, created.id], dashboardToConfig(created));
      upsertDashboardInList(queryClient, cid, created);
    }
  });
};

// All of a company's dashboards (metadata) — backs the dashboard list page.
export const useDashboardList = (cid: string) =>
  useQuery({
    ...dashboardListQueryOptions(cid),
    enabled: Boolean(cid)
  });

export const useDeleteDashboard = (cid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: DashboardId) =>
      DashboardingLambdaFetch('/deleteDashboard', {
        method: 'DELETE',
        params: { id, company: cid }
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: dashboardListKey(cid) })
  });
};

// The gallery's template list — backs TemplateGalleryModal.
export const useDashboardTemplates = (cid: string) =>
  useQuery({
    queryKey: ['dashboard-templates', cid],
    queryFn: () => fetchDashboardTemplates(cid),
    enabled: Boolean(cid)
  });

export const useInstantiateDashboardTemplate = (cid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: DashboardTemplateId) => instantiateDashboardTemplate(cid, templateId),
    onSuccess: ({ dashboard }) => upsertDashboardInList(queryClient, cid, dashboard)
  });
};
